import { Fragment, type ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { Translatable } from "app/pages/sales-intelligence/types";
import { haveSameElements, elementsAreInTheSameOrder } from "app/pages/sales-intelligence/utils/arrays";
import { useTranslation } from "pro-features/components/WithTranslation";

import { columnsSelectionTrackingService } from "../../columnsSelectionTrackingService";
import { useColumnOptions } from "../../hooks/useColumnOptions";
import type { ColumnPickerPluginConfig } from "../../plugins/types";
import { usePluginRegistry } from "../../plugins/usePluginRegistry";
import type { BaseColumnCategory, SearchableSelectionOption } from "../../types";
import {
    NotSelectedColumnOptionsList,
    type RestoredColumnToken,
} from "../ColumnOptionsList/NotSelectedColumnOptionsList";
import { SelectedColumnOptionsList } from "../ColumnOptionsList/SelectedColumnOptionsList";
import type { ColumnsCategorySelectProps } from "../ColumnsCategorySelect/ColumnsCategorySelect";
import { ColumnsCategorySelect } from "../ColumnsCategorySelect/ColumnsCategorySelect";
import { ColumnSearchField } from "../ColumnsSearchField/ColumnSearchField";
import { SelectedColumnsListHeading } from "../SelectedColumnsListHeading/SelectedColumnsListHeading";

import {
    CancelButton,
    ColumnsContainer,
    ColumnsContainerHeader,
    CUSTOM_MODAL_STYLES,
    StyledContentWrapper,
    StyledSelectedColumnsHeader,
} from "./styles";

const DEFAULT_SELECTED_CATEGORY: BaseColumnCategory = "all";

type Props<O> = {
    isOpened: boolean;
    initialSelectedColumnIds: readonly string[];
    columnOptions: readonly O[];
    title: Readonly<Translatable>;
    isApplyPending: boolean;
    pluginConfig?: ColumnPickerPluginConfig | null;
    onCancel: VoidFunction;
    onApply: (selectedNames: readonly string[]) => void;
} & Pick<ColumnsCategorySelectProps, "categoryConfig">;

const ColumnsSelectionModal = <O extends SearchableSelectionOption>(props: Props<O>): ReactElement => {
    const {
        columnOptions,
        isOpened,
        initialSelectedColumnIds,
        title,
        categoryConfig,
        pluginConfig,
        isApplyPending,
        onCancel,
        onApply,
    } = props;

    const translate = useTranslation();
    const { getLocale } = useTranslate();

    const [selectedIds, setSelectedIds] = useState(initialSelectedColumnIds);
    const [selectedCategory, setSelectedCategory] = useState<BaseColumnCategory | O["category"]>(
        DEFAULT_SELECTED_CATEGORY,
    );
    const [searchTerm, setSearchTerm] = useState("");

    const { selectedOptions, notSelectedOptions, appliedSearchTerm } = useColumnOptions({
        columnOptions,
        selectedIds,
        selectedCategory,
        searchTerm,
        locale: getLocale(),
        isAnyCatagegorySelected: selectedCategory === DEFAULT_SELECTED_CATEGORY,
    });

    const isNotDirty = useMemo(() => {
        return (
            haveSameElements(initialSelectedColumnIds, selectedIds) &&
            elementsAreInTheSameOrder(initialSelectedColumnIds, selectedIds)
        );
    }, [initialSelectedColumnIds, selectedIds]);

    const [restoredColumn, setRestoredColumn] = useState<RestoredColumnToken | null>(null);

    /**
     * Adding or removing a column shifts every row below it, and that shift should animate. Changing
     * the search or category also shifts rows, but to unrelated positions — animating that would look
     * like the list sliding around on every keystroke, so the gate is closed on those paths.
     */
    const [isLayoutAnimating, setIsLayoutAnimating] = useState(false);

    /**
     * The functional updaters are not stylistic: reading `selectedIds` from the closure meant two
     * clicks landing in the same batch both computed from the same stale array, so the first column
     * was silently dropped. Keeping these stable is also what lets the rows memoise.
     */
    const unselectColumnOption = useCallback((columnName: string) => {
        setIsLayoutAnimating(true);
        setSelectedIds((ids) => ids.filter((name) => name !== columnName));
        setRestoredColumn((previous) => ({ id: columnName, seq: (previous?.seq ?? 0) + 1 }));
        columnsSelectionTrackingService.trackRemoveColumn(columnName);
    }, []);

    const selectColumnOption = useCallback((columnName: string) => {
        setIsLayoutAnimating(true);
        setSelectedIds((ids) => ids.concat(columnName));
        columnsSelectionTrackingService.trackAddColumn(columnName);
    }, []);

    const { getColumnPluginsForSlot, getHeaderPluginsForSlot, getListPluginsForSlot } = usePluginRegistry(pluginConfig);

    const headerActionPlugins = useMemo(() => getHeaderPluginsForSlot("header-action"), [getHeaderPluginsForSlot]);
    const columnListBadgePlugins = useMemo(
        () => getListPluginsForSlot("column-list-badge", { columns: selectedOptions }),
        [getListPluginsForSlot, selectedOptions],
    );

    const reOrderColumnOption = (columnNames: string[]) => {
        setSelectedIds(columnNames);
    };

    const resetStates = () => {
        setIsLayoutAnimating(false);
        setSearchTerm("");
        setSelectedCategory(DEFAULT_SELECTED_CATEGORY);
        setSelectedIds(initialSelectedColumnIds);
    };

    const handleApply = () => {
        onApply(selectedIds);
        columnsSelectionTrackingService.trackApplyChanges(selectedOptions.length);
        resetStates();
    };

    const handleCancel = () => {
        onCancel();
        resetStates();
    };

    const handleSelectCategory = (category: string): void => {
        setIsLayoutAnimating(false);
        setSelectedCategory(category);
        setSearchTerm("");
    };

    const handleSearchTermChange = (newSearchTerm: string) => {
        setIsLayoutAnimating(false);
        setSearchTerm(newSearchTerm);
    };

    useEffect(() => {
        if (
            !haveSameElements(initialSelectedColumnIds, selectedIds) ||
            !elementsAreInTheSameOrder(initialSelectedColumnIds, selectedIds)
        ) {
            setSelectedIds(initialSelectedColumnIds);
        }
    }, [initialSelectedColumnIds]);

    return (
        <Modal
            isOpen={isOpened}
            shouldCloseOnOverlayClick={false}
            customStyles={CUSTOM_MODAL_STYLES}
        >
            <Modal.Header>
                <Stack>
                    <Text
                        data-automation="title-text"
                        fontVariant="h5"
                        color={colorsPalettes.carbon[500]}
                        margin="0 0 8px 0"
                    >
                        {translate(title.key, title.replacements)}
                    </Text>

                    <Text
                        data-automation="subtitle-text"
                        fontVariant="report"
                        color={colorsPalettes.carbon[500]}
                        margin="0 0 8px 0"
                    >
                        {translate("si.components.advanced_search_results_table.column_picker.subtitle")}
                    </Text>
                </Stack>

                {headerActionPlugins.length > 0 ? (
                    <Flex
                        alignItems="center"
                        gap="8px"
                        margin="0 0 0 auto"
                    >
                        {headerActionPlugins.map((plugin, index) => (
                            <Fragment key={index}>{plugin.component}</Fragment>
                        ))}
                    </Flex>
                ) : null}
            </Modal.Header>

            <Modal.Content>
                <StyledContentWrapper>
                    <ColumnsContainer>
                        <ColumnsContainerHeader>
                            <Text
                                data-automation="column-container-heading-text"
                                fontVariant="report"
                                color={colorsPalettes.carbon[400]}
                            >
                                {translate(
                                    "si.components.advanced_search_results_table.column_picker.columns_to_add_list.title",
                                )}
                            </Text>

                            <ColumnsCategorySelect
                                value={selectedCategory}
                                categoryConfig={categoryConfig}
                                onChange={handleSelectCategory}
                            />
                        </ColumnsContainerHeader>
                        <ColumnSearchField
                            value={searchTerm}
                            onChange={handleSearchTermChange}
                        />
                        <NotSelectedColumnOptionsList
                            columnOptions={notSelectedOptions}
                            searchTerm={appliedSearchTerm}
                            filterKey={`${selectedCategory}|${appliedSearchTerm}`}
                            isLayoutAnimating={isLayoutAnimating}
                            restoredColumn={restoredColumn}
                            getColumnPluginsForSlot={getColumnPluginsForSlot}
                            onColumnOptionClick={selectColumnOption}
                        />
                    </ColumnsContainer>

                    <ColumnsContainer>
                        <StyledSelectedColumnsHeader>
                            <SelectedColumnsListHeading columnsCount={selectedOptions.length} />

                            {columnListBadgePlugins.length > 0 ? (
                                <Flex
                                    alignItems="center"
                                    gap="4px"
                                >
                                    {columnListBadgePlugins.map((plugin, index) => (
                                        <Fragment key={index}>{plugin.component}</Fragment>
                                    ))}
                                </Flex>
                            ) : null}
                        </StyledSelectedColumnsHeader>
                        <DndProvider backend={HTML5Backend}>
                            <SelectedColumnOptionsList
                                columnOptions={selectedOptions}
                                getColumnPluginsForSlot={getColumnPluginsForSlot}
                                onColumnOptionClick={unselectColumnOption}
                                onColumnOptionReorder={reOrderColumnOption}
                            />
                        </DndProvider>
                    </ColumnsContainer>
                </StyledContentWrapper>
            </Modal.Content>

            <Modal.Footer>
                <CancelButton
                    type="flat"
                    isDisabled={isApplyPending}
                    onClick={handleCancel}
                >
                    {translate("si.common.button.close")}
                </CancelButton>
                <Button
                    isDisabled={isNotDirty}
                    isLoading={isApplyPending}
                    onClick={handleApply}
                >
                    {translate("si.common.button.apply")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ColumnsSelectionModal;
